import {
  CRAFTING_RECIPES,
  ITEM_CATALOG,
  ItemId,
  isGear,
  pickaxeTierToItem,
  type HotbarState,
  type InventorySlot,
  type Recipe,
} from './catalog';
import { PickaxeTier, type PersistentPlayerProfile } from '../types';

export type InventoryBag = 'run' | 'bank';

/**
 * Dual-bag inventory:
 *  - runBag: materials gathered this dive (wiped on death)
 *  - bankBag: extracted / persistent materials
 *  - ownedGear: tools & weapons that survive death
 *  - hotbar: quick-select action bar (1–5)
 */
export class InventoryManager {
  private readonly runBag = new Map<ItemId, number>();
  private readonly bankBag = new Map<ItemId, number>();
  private readonly ownedGear = new Set<ItemId>();
  private readonly hotbar: HotbarState = {
    slots: [ItemId.PickaxeRusty, null, null, null, null],
    activeIndex: 0,
  };

  private listeners = new Set<() => void>();

  constructor() {
    this.ownedGear.add(ItemId.PickaxeRusty);
    this.ownedGear.add(ItemId.SwordRusty);
    this.hotbar.slots = [ItemId.PickaxeRusty, ItemId.SwordRusty, null, null, null];
  }

  onChange(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    for (const l of this.listeners) l();
  }

  // ---------------------------------------------------------------------------
  // Queries
  // ---------------------------------------------------------------------------

  getRunAmount(id: ItemId): number {
    return this.runBag.get(id) ?? 0;
  }

  getBankAmount(id: ItemId): number {
    return this.bankBag.get(id) ?? 0;
  }

  /** Total available for crafting (run + bank). */
  getAvailable(id: ItemId): number {
    if (isGear(id)) return this.ownedGear.has(id) ? 1 : 0;
    return this.getRunAmount(id) + this.getBankAmount(id);
  }

  ownsGear(id: ItemId): boolean {
    return this.ownedGear.has(id);
  }

  listRunSlots(): InventorySlot[] {
    return this.mapToSlots(this.runBag);
  }

  listBankSlots(): InventorySlot[] {
    return this.mapToSlots(this.bankBag);
  }

  listOwnedGear(): ItemId[] {
    return [...this.ownedGear].sort((a, b) =>
      ITEM_CATALOG[a].name.localeCompare(ITEM_CATALOG[b].name),
    );
  }

  getHotbar(): Readonly<HotbarState> {
    return this.hotbar;
  }

  getActiveItemId(): ItemId | null {
    return this.hotbar.slots[this.hotbar.activeIndex] ?? null;
  }

  // ---------------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------------

  addToRun(id: ItemId, amount: number): void {
    if (amount <= 0 || isGear(id)) return;
    this.runBag.set(id, this.getRunAmount(id) + amount);
    this.notify();
  }

  addToBank(id: ItemId, amount: number): void {
    if (amount <= 0 || isGear(id)) return;
    this.bankBag.set(id, this.getBankAmount(id) + amount);
    this.notify();
  }

  /**
   * Spend materials preferring run bag first, then bank.
   * Returns false if insufficient.
   */
  trySpend(id: ItemId, amount: number): boolean {
    if (amount <= 0) return true;
    if (this.getAvailable(id) < amount) return false;

    let remaining = amount;
    const run = this.getRunAmount(id);
    if (run > 0) {
      const take = Math.min(run, remaining);
      const next = run - take;
      if (next <= 0) this.runBag.delete(id);
      else this.runBag.set(id, next);
      remaining -= take;
    }
    if (remaining > 0) {
      const bank = this.getBankAmount(id);
      const next = bank - remaining;
      if (next <= 0) this.bankBag.delete(id);
      else this.bankBag.set(id, next);
    }
    this.notify();
    return true;
  }

  /** Remove materials from the run bag only (shop sales). */
  trySpendFromRun(id: ItemId, amount: number): boolean {
    if (amount <= 0) return true;
    if (isGear(id)) return false;
    const have = this.getRunAmount(id);
    if (have < amount) return false;
    const next = have - amount;
    if (next <= 0) this.runBag.delete(id);
    else this.runBag.set(id, next);
    this.notify();
    return true;
  }

  grantGear(id: ItemId): void {
    if (!isGear(id)) return;
    this.ownedGear.add(id);
    this.ensureHotbarHas(id);
    this.notify();
  }

  /** Wipe run materials only — gear and bank survive. */
  clearRunBag(): void {
    this.runBag.clear();
    this.notify();
  }

  /** Snapshot + clear the run bag (used for death loot drops). */
  drainRunBag(): InventorySlot[] {
    const slots = this.listRunSlots();
    this.runBag.clear();
    if (slots.length > 0) this.notify();
    return slots;
  }

  /** Restore materials into the run bag (loot recovery). */
  addRunSlots(slots: readonly InventorySlot[]): void {
    let changed = false;
    for (const slot of slots) {
      if (slot.amount <= 0 || isGear(slot.itemId)) continue;
      this.runBag.set(slot.itemId, this.getRunAmount(slot.itemId) + slot.amount);
      changed = true;
    }
    if (changed) this.notify();
  }

  /** Move all run materials into the bank (successful extract). */
  bankRunMaterials(): void {
    for (const [id, amount] of this.runBag) {
      this.bankBag.set(id, this.getBankAmount(id) + amount);
    }
    this.runBag.clear();
    this.notify();
  }

  setActiveHotbar(index: number): boolean {
    if (index < 0 || index >= this.hotbar.slots.length) return false;
    if (!this.hotbar.slots[index]) return false;
    this.hotbar.activeIndex = index;
    this.notify();
    return true;
  }

  /** Place owned gear into a hotbar slot (or clear with null). */
  assignHotbar(index: number, itemId: ItemId | null): boolean {
    if (index < 0 || index >= this.hotbar.slots.length) return false;
    if (itemId !== null) {
      if (!this.ownedGear.has(itemId)) return false;
      // Remove duplicates elsewhere on the bar.
      for (let i = 0; i < this.hotbar.slots.length; i++) {
        if (this.hotbar.slots[i] === itemId) this.hotbar.slots[i] = null;
      }
    }
    this.hotbar.slots[index] = itemId;
    if (!this.hotbar.slots[this.hotbar.activeIndex]) {
      const first = this.hotbar.slots.findIndex((s) => s !== null);
      this.hotbar.activeIndex = first >= 0 ? first : 0;
    }
    this.notify();
    return true;
  }

  syncProfile(profile: PersistentPlayerProfile): void {
    const active = this.getActiveItemId();
    const def = active ? ITEM_CATALOG[active] : null;
    if (def?.pickaxeTier !== undefined) {
      profile.equippedPickaxeTier = def.pickaxeTier;
    }
    const unlocked = new Set<PickaxeTier>();
    for (const id of this.ownedGear) {
      const tier = ITEM_CATALOG[id].pickaxeTier;
      if (tier !== undefined) unlocked.add(tier);
    }
    if (unlocked.size === 0) unlocked.add(PickaxeTier.Rusty);
    // Profile interface exposes ReadonlySet; mutate via unknown bridge.
    (profile as unknown as { unlockedPickaxeTiers: Set<PickaxeTier> }).unlockedPickaxeTiers =
      unlocked;
  }

  /** Seed from an existing profile (e.g. after load). */
  hydrateFromProfile(profile: PersistentPlayerProfile): void {
    this.ownedGear.clear();
    for (const tier of profile.unlockedPickaxeTiers) {
      this.ownedGear.add(pickaxeTierToItem(tier));
    }
    if (this.ownedGear.size === 0) this.ownedGear.add(ItemId.PickaxeRusty);
    // Starter sidearm so combat is available before forging upgrades.
    this.ownedGear.add(ItemId.SwordRusty);
    const equipped = pickaxeTierToItem(profile.equippedPickaxeTier);
    this.hotbar.slots = [equipped, ItemId.SwordRusty, null, null, null];
    this.hotbar.activeIndex = 0;
    this.notify();
  }

  /** Restore bank, gear, hotbar, and run bag from a persisted snapshot. */
  hydrateFromSave(data: {
    bank: ReadonlyArray<InventorySlot>;
    ownedGear: ReadonlyArray<ItemId>;
    hotbar: Readonly<HotbarState>;
    runBag: ReadonlyArray<InventorySlot>;
  }): void {
    this.bankBag.clear();
    for (const slot of data.bank) {
      if (slot.amount <= 0 || isGear(slot.itemId)) continue;
      this.bankBag.set(slot.itemId, (this.bankBag.get(slot.itemId) ?? 0) + slot.amount);
    }

    this.ownedGear.clear();
    for (const id of data.ownedGear) {
      if (isGear(id)) this.ownedGear.add(id);
    }
    if (this.ownedGear.size === 0) this.ownedGear.add(ItemId.PickaxeRusty);
    this.ownedGear.add(ItemId.SwordRusty);

    this.runBag.clear();
    for (const slot of data.runBag) {
      if (slot.amount <= 0 || isGear(slot.itemId)) continue;
      this.runBag.set(slot.itemId, (this.runBag.get(slot.itemId) ?? 0) + slot.amount);
    }

    const slots: Array<ItemId | null> = [null, null, null, null, null];
    for (let i = 0; i < 5; i++) {
      const id = data.hotbar.slots[i] ?? null;
      slots[i] = id && this.ownedGear.has(id) ? id : null;
    }
    if (!slots.some((s) => s !== null)) {
      const pick =
        [...this.ownedGear].find((id) => ITEM_CATALOG[id].pickaxeTier !== undefined) ??
        ItemId.PickaxeRusty;
      slots[0] = pick;
      slots[1] = this.ownedGear.has(ItemId.SwordRusty) ? ItemId.SwordRusty : null;
    }
    this.hotbar.slots = slots;
    const preferred = data.hotbar.activeIndex;
    this.hotbar.activeIndex =
      preferred >= 0 && preferred < 5 && slots[preferred]
        ? preferred
        : Math.max(0, slots.findIndex((s) => s !== null));

    this.notify();
  }

  // ---------------------------------------------------------------------------
  // Internals
  // ---------------------------------------------------------------------------

  private ensureHotbarHas(id: ItemId): void {
    if (this.hotbar.slots.includes(id)) return;
    const empty = this.hotbar.slots.findIndex((s) => s === null);
    if (empty >= 0) {
      this.hotbar.slots[empty] = id;
    }
  }

  private mapToSlots(map: Map<ItemId, number>): InventorySlot[] {
    return [...map.entries()]
      .filter(([, n]) => n > 0)
      .map(([itemId, amount]) => ({ itemId, amount }))
      .sort((a, b) => ITEM_CATALOG[a.itemId].name.localeCompare(ITEM_CATALOG[b.itemId].name));
  }
}

export interface CraftResult {
  ok: boolean;
  reason?: string;
  recipe?: Recipe;
}

export class CraftingService {
  constructor(
    private readonly inventory: InventoryManager,
    private readonly getWallet: () => number,
    private readonly spendWallet: (amount: number) => boolean,
  ) {}

  listRecipes(): readonly Recipe[] {
    return CRAFTING_RECIPES;
  }

  canCraft(recipe: Recipe): CraftResult {
    if (recipe.requiresOwned && !this.inventory.ownsGear(recipe.requiresOwned)) {
      return {
        ok: false,
        reason: `Requires ${ITEM_CATALOG[recipe.requiresOwned].name}.`,
        recipe,
      };
    }
    if (this.inventory.ownsGear(recipe.result) && isGear(recipe.result)) {
      return { ok: false, reason: 'Already owned.', recipe };
    }
    if (this.getWallet() < recipe.creditCost) {
      return { ok: false, reason: `Need ${recipe.creditCost} credits.`, recipe };
    }
    for (const cost of recipe.costs) {
      if (this.inventory.getAvailable(cost.itemId) < cost.amount) {
        return {
          ok: false,
          reason: `Need ${cost.amount}× ${ITEM_CATALOG[cost.itemId].name}.`,
          recipe,
        };
      }
    }
    return { ok: true, recipe };
  }

  craft(recipeId: string): CraftResult {
    const recipe = CRAFTING_RECIPES.find((r) => r.id === recipeId);
    if (!recipe) return { ok: false, reason: 'Unknown recipe.' };

    const check = this.canCraft(recipe);
    if (!check.ok) return check;

    for (const cost of recipe.costs) {
      if (!this.inventory.trySpend(cost.itemId, cost.amount)) {
        return { ok: false, reason: 'Spend failed.', recipe };
      }
    }
    if (recipe.creditCost > 0 && !this.spendWallet(recipe.creditCost)) {
      return { ok: false, reason: 'Insufficient credits.', recipe };
    }

    if (isGear(recipe.result)) {
      this.inventory.grantGear(recipe.result);
      // Auto-equip newly crafted gear into hotbar / active if tool.
      const hb = this.inventory.getHotbar();
      const idx = hb.slots.findIndex((s) => s === recipe.result);
      if (idx >= 0) this.inventory.setActiveHotbar(idx);
    } else {
      this.inventory.addToBank(recipe.result, recipe.resultAmount);
    }

    return { ok: true, recipe };
  }
}
